import { Injectable } from '@angular/core';
import { HttpClientCustom } from '../custom/http-client';
import { PlanData } from '../plans/plan-data';
import { environment } from 'src/environments/environment';

import { ClientModel } from 'src/models/client.model';
import { HttpParams } from '@angular/common/http';
import { Events } from '../events/events';

import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';

import { Client } from 'src/providers/client/client';
import { DemoAccountService } from 'src/shared/services/demo-account.service';

import { DateTime } from 'luxon';

import { ThemeService, ThemeMode } from 'src/shared/services/theme.service';
import { RentalModesService } from 'src/shared/services/rental-modes.service';

declare const _dataLayer: any;

@Injectable()
export class Me {

  constructor(
    private client: Client,
    private httpClient: HttpClientCustom,
    private planData: PlanData,
    private storage: TidyStorage,
    private events: Events,
    private onboardingProvider: OnboardingProvider,
    private aboutThisPageService: AboutThisPageService,
    private themeService: ThemeService,
    private demoAccountService: DemoAccountService,
    private rentalModesService: RentalModesService
  ) { }

  async load(getFromBackend = false): Promise<ClientModel> {
    const didRefreshDataAfterTIDY6Launch = localStorage.getItem('didRefreshDataAfterTIDY6Launch') === 'true';
    if (!didRefreshDataAfterTIDY6Launch) {
      localStorage.setItem('didRefreshDataAfterTIDY6Launch', 'true');
      getFromBackend = true;
    }
    const data = await this.getMyData(getFromBackend);
    await this.aboutThisPageService.fetchAndStoreAboutThisPageData();
    localStorage.setItem('isRentalClient', data?.customer_account?.account_type === 'rental' ? 'true' : 'false');
    localStorage.setItem('accountType', data?.customer_account?.account_type);
    localStorage.setItem('advancedMode', data?.customer_account?.advanced_mode ? 'true' : 'false');
    // Set isFirstBooking from backend - true if user has never completed a booking
    await this.storage.save('isFirstBooking', data?.customer_account?.first_booking === true);
    localStorage.setItem('user_role', data?.current_user?.role);
    localStorage.setItem('customer_id', data?.customer_account?.id);
    localStorage.setItem('customer_name', data?.current_user?.name);
    localStorage.setItem('customer_email', data?.current_user?.email);
    await this.demoAccountService.checkAndSetupDemoAccount(data?.current_user?.email);
    if (data?.customer_account.account_name !== '' && data?.customer_account?.account_name !== null) {
      localStorage.setItem('companyName', data?.customer_account?.account_name);
    }
    this.events.publish("updateMenu");
    this.events.publish("updateBackground");
    const shouldShowOnboarding = this.checkIfShouldShowOnboarding(data);
    if (shouldShowOnboarding) {
      if (data?.customer_account?.account_type === 'rental' || data?.customer_account?.account_type === 'residential') {
        await this.getOnboardingGoals(data);
      }
    }
    return data;
  }

  checkIfShouldShowOnboarding(data) {
    if (data?.customer_account?.account_type !== 'rental') {
      this.onboardingProvider.showOnboarding = false;
      return false;
    }
    const createdAt = DateTime.fromISO(data?.customer_account?.created_at);
    const cutoffDate = DateTime.fromObject({ year: 2025, month: 12, day: 19 });
    if (!createdAt.isValid || createdAt < cutoffDate) {
      this.onboardingProvider.showOnboarding = false;
      return false;
    }
    localStorage.setItem('showOnboarding', 'true');
    this.onboardingProvider.showOnboarding = true;
    return true;
  }

  async getOnboardingGoals(_data) {
    this.storage.save('showOnboarding', true);
    const serverOnboardingGoals = await this.storage.retrieve('onboardingGoals');
    if (!serverOnboardingGoals) {
      await this.onboardingProvider.getGoalsFromServer();
    }
  }

  async getAccType(): Promise<string> {
    const accType = localStorage.getItem('accountType');
    if (accType) {
      return accType;
    }

    await this.load();
    return localStorage.getItem('accountType') || '';
  }

  sendFeedback(data, jobId): Promise<any> {
    const url = `api/v1/me/jobs/${jobId}/feedback`;

    return this.httpClient.post(url, data);
  }

  async getMyData(getFromBackend = false): Promise<any> {
    if (!getFromBackend) {
      const storedData = await this.storage.retrieve('me');
      if (storedData) {
        return storedData;
      }
    }
    try {
      const currentLanguage = await this.client.getMetadata(['external.language']);
      const language = currentLanguage?.[0]?.value || 'en';
      localStorage.setItem('language', language);
    } catch(_err) {
      localStorage.setItem('language', 'en');
    }
    try {
      const themeMetadata = await this.client.getMetadata(['external.theme_mode']);
      const savedTheme = themeMetadata?.[0]?.value as ThemeMode;
      if (savedTheme === 'dark' || savedTheme === 'light') {
        this.themeService.setTheme(savedTheme);
        localStorage.setItem('tidy_theme_user_set', 'true');
      }
    } catch(_err) {
      // Silently fail - theme metadata loading is not critical
    }
    // Restore the account's rental modes (long/short-term toggles) the same way
    // the theme is restored — the service itself no-ops for demo sessions.
    await this.rentalModesService.hydrateFromMetadata();
    const data = await this.client.getMe(['permissions'], getFromBackend);

    return data;
  }

  alreadyCancelledCleaningWithin24hours(): Promise<any> {
    return this.client.getMe(['already_cancelled_cleaning_within_24hours'], true, false);
  }

  getName() {
    return localStorage.getItem('customer_name');
  }

  async getMetadata(key){
    const query = new HttpParams({fromObject: {keys:[`${key}`]}}).toString();
    const url = `api/v1/customer/metadata?${query}`;
    return await this.httpClient.get(url);
  }

  async getHasNegativeBalance() {
    const url = 'api/v1/customer/metadata?keys[]=negative_balance_since';
    const response = await this.httpClient.get(url);
    if (!response[0]?.value) {
      return false;
    }
    const negativeBalanceSince = DateTime.fromSQL(response[0].value.replace(' UTC', ''), { zone: 'utc' });
    const now = DateTime.now();
    const daysSinceNegativeBalance = now.diff(negativeBalanceSince, 'days').days;
    return daysSinceNegativeBalance > 1;
  }

  async shareContent() {
    const me = await this.getMyData();
    const firstName = me.name.split(" ")[0];
    const referralCode = me.referral_code;
    const appUrl = environment.customer_portal;

    const url = `${appUrl}booking-signup/signUp/${referralCode}`;

    const message = `${firstName} has given you a FREE $10 credit for a TIDY home cleaning. To claim your gift, sign up using this link: ${url}`

    return {message, url};
  }

  getSubscriptionChangePreview(newPlanTypeKey) {
    const url = `api/v1/customer/subscription/preview-plan-change-balance?new_plan_type_key=${newPlanTypeKey}`;
    return this.httpClient.get(url);
  }


  getSubscriptions() {
    const url = 'api/v1/customer/subscription/subscriptions?includes=customer_address_count';
    return this.httpClient.get(url);
  }

  getCurrentSubscription(subscriptions) {
    let currentSubscription = null;
    if (subscriptions.length > 1) {
      subscriptions.map((subscription) => {
        if (subscription.is_activated_in_next_cycle) {
          currentSubscription = subscription;
        }
      });
    } else {
      currentSubscription = subscriptions[0];
    }
    return currentSubscription;
  }

  getSubscriptionTypes() {
    const url = 'api/v1/customer/subscription/plan-types';
    return this.httpClient.get(url);
  }

  changeSubscription(data) {
    const url = 'api/v1/customer/subscriptions/subscribe';
    return this.httpClient.put(url, data);
  }

  getSubscriptionPreview(_planTypeKeys) {
    const planTypeKey = 'customer_app_yearly_subscription_1';
    const url = `api/v1/customer/subscription/preview-plan-change-balance?new_plan_type_key=${planTypeKey}&includes=usage`;
    return this.httpClient.get(url);
  }

  getSubscriptionInvoices() {
    const url = 'api/v1/customer/subscription/subscription-invoices';
    return this.httpClient.get(url);
  }

  getBookingFormSelectValue(data) {
    const keys = Object.keys(data);
    return data[keys[0]];
  }

  getBookingFormMultiselectValue(data) {
    let string = '';
    data.map((item, i) => {
      const keys = Object.keys(item);
      string += item[keys[0]];
      if (i < data.length - 1) {
        string += ', ';
      }
    });
    return string;
  }

  endSubscriptionFreeTrial(subscriptionPlanId) {
    const payload = {
      plan_subscription_id: subscriptionPlanId
    }
    const url = 'api/v1/customer/subscription/end-trial';
    return this.httpClient.put(url, payload);
  }


  async checkIfHasNonTrialPaidSubscription(subscriptionTypes) {
    let hasNonTrialPaidSubscription = localStorage.getItem('hasNonTrialPaidSubscription') === 'true';
    if (hasNonTrialPaidSubscription) {
      return true;
    }
    const subscriptions = await this.getSubscriptions();
    const currentSubscription = subscriptions.length === 1 ?
      subscriptions[0] : 
      subscriptions.find((item) => item.is_activated_in_next_cycle);
    if (!currentSubscription?.plan) {
      return false;
    }
    const planType = subscriptionTypes.find((type) => type.key === currentSubscription?.plan.key)
    hasNonTrialPaidSubscription = this.checkIfIsOTCDiscountSubscription(currentSubscription?.plan?.key, currentSubscription?.plan.trial_days);
    this.storage.save('currentSubscription', planType);
    localStorage.setItem('hasNonTrialPaidSubscription', hasNonTrialPaidSubscription ? 'true' : 'false');
    return hasNonTrialPaidSubscription;
  }

  checkIfIsOTCDiscountSubscription(key, trialDays) {
    if (!key) {
      return false;
    } else if (this.getSubscriptionType(key) === 'custom') {
      return true;
    } else if (trialDays === 0 && !key.includes('free')) {
      return true;
    } else {
      return false;
    }
  }

  getSubscriptionType(key) {
    const types = {
      'flex': 'Flex',
      'free_tier_yearly_subscription_not_trial': 'Free',
      'tidy_plus_yearly_subscription_not_trial': 'TIDY+',
      'tidy_plus_yearly_subscription_trial': 'TIDY+',
      'tidy_plus_monthly_subscription_not_trial': 'TIDY+',
      'tidy_plus_monthly_subscription_trial': 'TIDY+',
      'standard_yearly_subscription_not_trial': 'Standard',
      'standard_yearly_subscription_trial': 'Standard',
      'standard_monthly_subscription_not_trial': 'Standard',
      'standard_monthly_subscription_trial': 'Standard',
      'advanced_yearly_subscription_not_trial': 'Advanced',
      'advanced_yearly_subscription_trial': 'Advanced',
      'advanced_monthly_subscription_not_trial': 'Advanced',
      'advanced_monthly_subscription_trial': 'Advanced'
    }
    return types[key] || 'custom';
  }

}